import React, { useEffect, useRef, useState } from 'react';
import api from '../lib/api';
import { Send, Trash2, Sparkles } from 'lucide-react';

export default function ChatPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [persona, setPersona] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    api.get('/api/chat/history').then(({ data }) => setMessages(data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, sending]);

  const send = async (e) => {
    e?.preventDefault();
    const msg = input.trim();
    if (!msg || sending) return;
    setInput('');
    const optimistic = { message_id: `local_${Date.now()}`, role: 'user', content: msg, created_at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    setSending(true);
    try {
      const { data } = await api.post('/api/chat', { message: msg });
      setMessages((m) => [...m, { message_id: data.message_id || `srv_${Date.now()}`, role: 'assistant', content: data.content, created_at: new Date().toISOString() }]);
    } catch (err) {
      setMessages((m) => [...m, { message_id: `err_${Date.now()}`, role: 'assistant', content: `Sorry, something went wrong: ${err.message}`, created_at: new Date().toISOString() }]);
    } finally { setSending(false); }
  };

  const clearHistory = async () => {
    if (!window.confirm('Clear your conversation history?')) return;
    try { await api.delete('/api/chat/history'); setMessages([]); } catch {}
  };

  const suggestions = [
    'What is inherent worth?',
    'Tell me about the three pillars',
    'How do I love myself unconditionally?',
    'What does self-honesty really mean?',
  ];

  return (
    <div className="flex flex-col h-screen lg:h-[100vh]">
      <header className="flex items-center justify-between px-6 py-4 border-b border-ee-border bg-ee-bg2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gold/10 border border-gold/40 flex items-center justify-center"><Sparkles size={18} className="text-gold" /></div>
          <div>
            <div className="text-white font-serif text-lg">EE</div>
            <div className="text-xs text-ee-muted">Your Elemental Embrace companion</div>
          </div>
        </div>
        <button onClick={clearHistory} className="text-xs text-ee-muted hover:text-red-400 inline-flex items-center gap-1"><Trash2 size={14} /> Clear</button>
      </header>

      <div className="flex-1 overflow-y-auto p-6 space-y-5">
        {loading && <div className="text-center text-ee-muted text-sm">Loading conversation…</div>}
        {!loading && messages.length === 0 && (
          <div className="max-w-2xl mx-auto text-center mt-12">
            <div className="w-16 h-16 rounded-full bg-gold/10 border border-gold/40 flex items-center justify-center mx-auto mb-4"><Sparkles size={28} className="text-gold" /></div>
            <h2 className="hero-title text-3xl text-white mb-2">Hi, I'm EE</h2>
            <p className="text-ee-text2 mb-8">What's alive in you today? Try one of these prompts to begin.</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {suggestions.map((s) => (
                <button key={s} onClick={() => setInput(s)} className="ee-card p-4 text-left text-sm text-ee-text2 hover:border-gold/40 transition-colors">{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.message_id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${m.role === 'user' ? 'bg-gold/15 border border-gold/30 text-white rounded-br-sm' : 'bg-ee-card border border-ee-border text-ee-text2 rounded-bl-sm prose-ee'}`}>
              {m.content.split('\n').map((line, i) => <p key={i} className="mb-2 last:mb-0">{line}</p>)}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-ee-card border border-ee-border rounded-2xl rounded-bl-sm px-4 py-3 dot-anim"><span /><span /><span /></div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} className="p-4 border-t border-ee-border bg-ee-bg2">
        <div className="max-w-3xl mx-auto flex items-center gap-2 bg-ee-bg border border-ee-border rounded-full px-4 focus-within:border-gold transition-colors">
          <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask EE anything…" className="flex-1 bg-transparent py-3 outline-none text-white text-sm" />
          <button data-testid="chat-send" type="submit" disabled={sending || !input.trim()} className="w-10 h-10 rounded-full gold-bg flex items-center justify-center text-black disabled:opacity-40"><Send size={16} /></button>
        </div>
      </form>
    </div>
  );
}
